import {
  House, Database, ShieldCheck,
  Sparkle, Globe, SealCheck,
  ListChecks, FolderOpen, BookOpenText, ChartLineUp, Kanban, CurrencyDollar, Stack, Scales, Recycle, TreeStructure,
  Brain, MicrophoneStage, FileMagnifyingGlass, ChartBar, Broadcast, TrendUp, Lightning, Dna, Books,
} from "@phosphor-icons/react";
import { ADMIN_SECTIONS } from "@/lib/adminSections";

// Grouped navigation. Each group has a mono-uppercase section label and items.
// `badge` keys map to counts from GET /nav/badges (rendered as a small count pill).
// Firm navigation (Phase 1-3 IA merge): the four AI working surfaces are unified
// under Studio; the five insight surfaces under Insights; the five library/review
// stores under Knowledge Hub; the three compliance references under Compliance
// Reference. Legacy top-level routes still resolve (redirects in App.js) so
// deep-links, ⌘K search and command-palette actions keep working.
export const NAV_GROUPS = [
  {
    key: "command-center",
    label: "Command Center",
    items: [
      { key: "dashboard", label: "Command Center", to: "/", icon: House, perm: "dashboard:view", badge: "needs_attention", badgeTone: "attention" },
    ],
  },
  {
    key: "clients",
    label: "Clients & Pipeline",
    items: [
      { key: "intel-ghana", label: "Global Intelligence", to: "/intelligence", icon: Globe, perm: "dashboard:view", badge: "intel_needs_review", badgeTone: "neutral" },
      { key: "pipeline", label: "Pipeline", to: "/pipeline", icon: Kanban, perm: "dashboard:view" },
      { key: "pricing", label: "Pricing", to: "/pricing", icon: CurrencyDollar, perm: "dashboard:view" },
      { key: "workspaces", label: "Client Workspaces", to: "/workspaces", icon: FolderOpen, perm: "dashboard:view" },
    ],
  },
  {
    key: "ai-knowledge",
    label: "AI & Knowledge",
    items: [
      { key: "studio", label: "Studio", to: "/studio", icon: Sparkle, perm: "dashboard:view", ai: true },
      { key: "insights", label: "Insights", to: "/insights", icon: ChartLineUp, perm: "reports:view", ai: true },
      { key: "knowledge-hub", label: "Knowledge Hub", to: "/knowledge", icon: Books, perm: "dashboard:view", badge: "output_approvals", badgeTone: "attention" },
      { key: "compliance-ref", label: "Compliance Reference", to: "/compliance-ref", icon: Scales, perm: "dashboard:view" },
    ],
  },
  {
    key: "admin",
    label: "Administration",
    items: [
      { key: "admin", label: "Administration", to: "/admin", icon: SealCheck, perm: "audit:view" },
    ],
  },
];

// Flat list of navigable (non-coming-soon) items for the command palette.
// Admin sub-sections are appended so they remain searchable from ⌘K. We also
// synthesise the pre-merge names (Ask DAAKYI, Meeting Copilot, Policy Analyzer,
// Analytics, Radar, Forecast, Autonomous, Firm DNA) so keyboard-driven users
// keep their muscle memory — they deep-link into the correct Studio/Insights tab.
const HUB_SHORTCUTS = [
  { key: "ask", label: "Kasara — Ask DAAKYI", to: "/studio?tab=ask", icon: Brain, perm: "dashboard:view", ai: true },
  { key: "specialists", label: "AI Specialists", to: "/studio", icon: Sparkle, perm: "dashboard:view", ai: true },
  { key: "meetings", label: "Meeting Copilot", to: "/studio?assistant=meetings", icon: MicrophoneStage, perm: "dashboard:view", ai: true },
  { key: "policy-analyzer", label: "Policy Analyzer", to: "/studio?assistant=grc&view=policy", icon: FileMagnifyingGlass, perm: "dashboard:view", ai: true },
  { key: "analytics", label: "Analytics", to: "/insights?tab=analytics", icon: ChartBar, perm: "reports:view", ai: true },
  { key: "radar", label: "Threat & Reg Radar", to: "/insights?tab=radar", icon: Broadcast, perm: "reports:view", ai: true },
  { key: "forecast", label: "Posture Forecast", to: "/insights?tab=forecast", icon: TrendUp, perm: "reports:view", ai: true },
  { key: "autonomous", label: "Autonomous Assurance", to: "/insights?tab=autonomous", icon: Lightning, perm: "reports:view", ai: true },
  { key: "firm-dna", label: "Firm DNA Health", to: "/insights?tab=firm-dna", icon: Dna, perm: "reports:view", ai: true },
  { key: "reuse", label: "Prior Art (Reuse)", to: "/knowledge?tab=library&sub=prior-art", icon: Recycle, perm: "dashboard:view", ai: true },
  { key: "outputs", label: "Review Queue", to: "/knowledge?tab=review", icon: ListChecks, perm: "dashboard:view" },
  { key: "knowledge", label: "Methodologies", to: "/knowledge?tab=knowledge", icon: Database, perm: "knowledge:view" },
  { key: "content-library", label: "Content Blocks", to: "/knowledge?tab=library&sub=content", icon: BookOpenText, perm: "library:view" },
  { key: "master-documents", label: "Document Assembly", to: "/knowledge?tab=master", icon: Stack, perm: "dashboard:view" },
  { key: "control-library", label: "Control Library", to: "/compliance-ref?tab=controls", icon: ShieldCheck, perm: "dashboard:view" },
  { key: "frameworks", label: "Frameworks", to: "/compliance-ref?tab=frameworks", icon: TreeStructure, perm: "dashboard:view" },
  { key: "regulations", label: "Regulatory Mapping", to: "/compliance-ref?tab=regulations", icon: Scales, perm: "dashboard:view" },
];

export const ALL_NAV_ITEMS = [
  ...NAV_GROUPS.flatMap((g) => g.items.filter((i) => i.to && !i.comingSoon)),
  ...HUB_SHORTCUTS,
  ...ADMIN_SECTIONS.map((s) => ({ key: `admin-${s.key}`, label: s.label, to: `/admin/${s.key}`, icon: s.icon, perm: s.perm })),
];

// Icons for command-palette quick actions / search result groups.
export { FolderOpen };

// Minimal navigation for invited client-portal users — only their own workspace.
export function clientNavGroups() {
  return [{
    key: "portal",
    label: "Portal",
    items: [
      { key: "my-workspace", label: "My Workspace", to: "/portal", icon: FolderOpen, perm: "dashboard:view" },
    ],
  }];
}

export const ROLE_LABELS = {
  super_admin: "Super Admin",
  platform_admin: "Platform Admin",
  workspace_owner: "Workspace Owner",
  contributor: "Contributor",
  reviewer: "Reviewer",
  viewer: "Viewer",
  client: "Client User",
  developer: "Preview Developer",
  qa_tester: "Preview QA Tester",
};
